import * as t from "io-ts";
import {
    bornCodec,
    commonIdCodec,
    condensedSpacesStringCodec,
    nonEmptyStringOrNullCodec,
    nullable,
    pgIntegerCodec,
    pgSmallintCodec,
    positiveNumberCodec,
    trimmedStringCodec,
} from "./common";
import { messages, Messages } from "../utils/i18n/i18n";
import { NonEmptyString, NumberFromString } from "io-ts-types";
import { assertUnreachable } from "../utils/assertUnreachable";
import { array, either, number, option, ord, record, string } from "fp-ts";
import { apply, flow, identity, pipe } from "fp-ts/function";
import { contramap, Ord } from "fp-ts/Ord";
import { Either } from "fp-ts/Either";
import {
    errorsToMessage,
    getOptionReverseOrd,
    optionOrChain,
    optNumberReverseOrd,
    rightOrThrow,
} from "../utils/fp";
import { Option } from "fp-ts/Option";
import {
    FullName,
    fullNameCodec,
    nameSegmentCodec,
    sexCodec,
    switchForenameAndSurname,
} from "./user";
import { table, TableRowT } from "../utils/pg-ts/pg-ts";
import { RaceId, raceTable } from "./event";
import { categoriesTable, CategoryId } from "./category";
import { serialPointsCodec } from "./series";
import { organizationAthleteTable } from "./organization";
import { clubsTable } from "./club";
import {
    StoredTimeEntry,
    storedTimeEntryCodec,
    timeEntryFormatCodec,
    timeEntryValidator,
} from "./timing";
import { Eq } from "fp-ts/lib/Eq";
import { sequenceS } from "fp-ts/Apply";

// enum ResultColumnKind {
//   TABLE_DEDICATED
//   UNKNOWN(string, time, number, enum_value)
// }
// column kind -> persisted, computed
// e.g computed rank from time
// e.g. computed serial points? mapping rank -> points

const rankCodec = t.intersection([pgIntegerCodec, positiveNumberCodec], "Rank");
export type Rank = t.TypeOf<typeof rankCodec>;

export const sheetNameCodec = t.string;
export type SheetName = t.TypeOf<typeof sheetNameCodec>;
export const importedRowCodec = t.UnknownRecord;
export type ImportedRawRow = t.TypeOf<typeof importedRowCodec>;
export interface ImportedRow {
    raw: ImportedRawRow;
    id: string; // for DataGrid purpose
    categoryId: Nullable<CategoryId>;
}
export type OriginalColName = string; // from sheet, usually "A", "B", etc...

const finishTimeColCodec = t.type({
    kind: t.literal("finishTime"),
    format: timeEntryFormatCodec,
});
type FinisTimeCol = t.TypeOf<typeof finishTimeColCodec>;

const startTimeColCodec = t.type({
    kind: t.literal("startTime"),
    format: timeEntryFormatCodec,
});
type StartTimeCol = t.TypeOf<typeof startTimeColCodec>;

const forenameSurnameColCodec = t.type({ kind: t.literal("forenameSurname") });
type ForenameSurnameCol = t.TypeOf<typeof forenameSurnameColCodec>;

const surnameForenameColCodec = t.type({ kind: t.literal("surnameForename") });
type SurnameForenameCol = t.TypeOf<typeof surnameForenameColCodec>;

const forenameColCodec = t.type({ kind: t.literal("forename") });
type ForenameCol = t.TypeOf<typeof forenameColCodec>;

const surnameColCodec = t.type({ kind: t.literal("surname") });
type SurnameCol = t.TypeOf<typeof surnameColCodec>;

const raceNumberColCodec = t.type({ kind: t.literal("raceNumber") });
type RaceNumberCol = t.TypeOf<typeof raceNumberColCodec>;

const bibColCodec = t.type({ kind: t.literal("bib") });
type BibCol = t.TypeOf<typeof bibColCodec>;

export const bornColCodec = t.type({ kind: t.literal("born") });
export type BornCol = t.TypeOf<typeof bornColCodec>;

const rankColCodec = t.type({ kind: t.literal("rank") });
type RankCol = t.TypeOf<typeof rankColCodec>;

export const sexColCodec = t.type({
    kind: t.literal("sex"),
    // TODO configurable mapping? e.g. "male", "female" -> "m", "ž"
});
export type SexCol = t.TypeOf<typeof sexColCodec>;

const categoryColCodec = t.type({
    kind: t.literal("category"),
});
type CategoryCol = t.TypeOf<typeof categoryColCodec>;

const teamColCodec = t.type({
    kind: t.literal("team"),
});
type TeamCol = t.TypeOf<typeof teamColCodec>;

export const commonColTitleCodec = NonEmptyString;

const commonTextColCodec = t.type({
    kind: t.literal("commonText"),
    title: commonColTitleCodec,
});
type CommonTextCol = t.TypeOf<typeof commonTextColCodec>;

const commonNumberColCodec = t.type({
    kind: t.literal("commonNumber"),
    title: commonColTitleCodec,
});
type CommonNumberCol = t.TypeOf<typeof commonNumberColCodec>;

const commonTimeEntryColCodec = t.type({
    kind: t.literal("commonTimeEntry"),
    title: commonColTitleCodec,
    format: timeEntryFormatCodec,
});
type CommonTimeEntryCol = t.TypeOf<typeof commonTimeEntryColCodec>;

const skipColCodec = t.type({
    kind: t.literal("skip"),
});

export const importedColumnCodec = t.union(
    [
        finishTimeColCodec,
        startTimeColCodec,
        forenameSurnameColCodec,
        surnameForenameColCodec,
        forenameColCodec,
        surnameColCodec,
        bornColCodec,
        rankColCodec,
        sexColCodec,
        categoryColCodec,
        teamColCodec,
        raceNumberColCodec,
        bibColCodec,
        commonTextColCodec,
        commonNumberColCodec,
        commonTimeEntryColCodec,
        skipColCodec,
    ],
    "ImportedColumn"
);

export type ImportedColumn = t.TypeOf<typeof importedColumnCodec>;
export type ImportedColumnKind = ImportedColumn["kind"];

export const columnKindLabels: Messages<Record<ImportedColumnKind, string>> = {
    en: {
        startTime: "Start time",
        finishTime: "Finish time", // relative to startTime | absolute
        forenameSurname: "Forename Surname",
        surnameForename: "Surname Forename",
        forename: "Forename",
        surname: "Surname",
        born: "Year of birth",
        // age: null, // absolute "born", relative "age" cannot be together
        rank: "Rank",
        sex: "Gender", // mapping
        category: "Category", // mapping
        team: "Team",
        bib: "Bib",
        raceNumber: "Start time",
        commonText: "Common text",
        commonNumber: "Common number",
        commonTimeEntry: "Common entry",

        skip: "Skip from import",
    },
    cs: {
        finishTime: "Výsledný čas",
        startTime: "Startovní čas",
        bib: "Čip/bib",
        raceNumber: "Startovní číslo",
        forenameSurname: "Jméno Příjmení",
        surnameForename: "Příjmení Jméno",
        forename: "Jméno",
        surname: "Příjmení",
        born: "Rok narození",
        rank: "Pořadí",
        sex: "Pohlaví",
        category: "Kategorie",
        team: "Tým", // TODO unify with athlete->club
        commonText: "Obecný text",
        commonNumber: "Obecné číslo",
        commonTimeEntry: "Obecný čas", // TODO in race with known splits, user can assign split time directly from select
        skip: "Neimportovat",
    },
};

// TODO
interface ResultListCol {
    kind: ImportedColumnKind;
}

const wrapValidatedValueWithKind =
    <K extends ImportedColumnKind, T>(kind: K) =>
    (validator: Validator<T>) =>
    (cellValue: unknown) =>
        pipe(
            cellValue,
            validator,
            either.map((validatedCellValue) => ({
                kind: kind,
                value: validatedCellValue,
            }))
        );
// TODO add lang parameter to localize errors?
type Validator<V> = (cellValue: unknown) => Either<string, V>;
type ValidatorType<V extends Validator<any>> = V extends Validator<infer T>
    ? T
    : never;
const validatorFromCodec = <T>(codec: t.Decoder<unknown, T>) =>
    flow(codec.decode, either.mapLeft(errorsToMessage));

const finishTimeValidator = (column: FinisTimeCol) =>
    pipe(
        column.format,
        timeEntryValidator,
        finishTimeEntryValidator,
        wrapValidatedValueWithKind(column.kind)
    );
type FinishTimeValue = ValidatorType<ReturnType<typeof finishTimeValidator>>;

const startTimeValidator = (column: StartTimeCol) =>
    pipe(
        column.format,
        timeEntryValidator,
        wrapValidatedValueWithKind(column.kind)
    );
type StartTimeValue = ValidatorType<ReturnType<typeof startTimeValidator>>;

export const bornValidator = (column: BornCol) =>
    pipe(
        trimmedStringCodec.pipe(NumberFromString.pipe(bornCodec)),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type BornValue = ValidatorType<ReturnType<typeof bornValidator>>;

const forenameSurnameValidator = (column: ForenameSurnameCol) =>
    pipe(
        trimmedStringCodec.pipe(condensedSpacesStringCodec.pipe(fullNameCodec)),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type ForenameSurnameValue = ValidatorType<
    ReturnType<typeof forenameSurnameValidator>
>;

const fullnameFromSurnameForenameCodec = new t.Type<FullName>(
    "fullnameFromSurnameForenameCodec",
    fullNameCodec.is,
    flow(fullNameCodec.validate, either.map(switchForenameAndSurname)),
    identity
);

const surnameForenameValidator = (column: SurnameForenameCol) =>
    pipe(
        trimmedStringCodec.pipe(
            condensedSpacesStringCodec.pipe(fullnameFromSurnameForenameCodec)
        ),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type SurnameForenameValue = ValidatorType<
    ReturnType<typeof surnameForenameValidator>
>;

const raceNumberValidator = (column: RaceNumberCol) =>
    pipe(
        trimmedStringCodec.pipe(raceNumberStringCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type RaceNumberValue = ValidatorType<ReturnType<typeof raceNumberValidator>>;

const bibValidator = (column: BibCol) =>
    pipe(
        trimmedStringCodec.pipe(bibCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type BibValue = ValidatorType<ReturnType<typeof bibValidator>>;

const forenameValidator = (column: ForenameCol) =>
    pipe(
        trimmedStringCodec.pipe(nameSegmentCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type ForenameValue = ValidatorType<ReturnType<typeof forenameValidator>>;

const surnameValidator = (column: SurnameCol) =>
    pipe(
        trimmedStringCodec.pipe(nameSegmentCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type SurnameValue = ValidatorType<ReturnType<typeof surnameValidator>>;

export const sexValidator = (column: SexCol) =>
    pipe(
        trimmedStringCodec.pipe(sexCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type SexValue = ValidatorType<ReturnType<typeof sexValidator>>;

const teamValidator = (column: TeamCol) =>
    pipe(
        nonEmptyStringOrNullCodec(trimmedStringCodec.pipe(NonEmptyString)),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type TeamValue = ValidatorType<ReturnType<typeof teamValidator>>;

const commonTextValidator = (column: CommonTextCol) =>
    pipe(
        trimmedStringCodec,
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type CommonTextValue = ValidatorType<ReturnType<typeof commonTextValidator>>;

const commonNumberValidator = (column: CommonNumberCol) =>
    pipe(
        trimmedStringCodec.pipe(NumberFromString),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type CommonNumberValue = ValidatorType<
    ReturnType<typeof commonNumberValidator>
>;

const rankValidator = (column: RankCol) =>
    pipe(
        trimmedStringCodec.pipe(NumberFromString).pipe(rankCodec),
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type RankValue = ValidatorType<ReturnType<typeof rankValidator>>;

const categoryValidator = (column: CategoryCol) =>
    pipe(
        trimmedStringCodec,
        validatorFromCodec,
        wrapValidatedValueWithKind(column.kind)
    );
type CategoryValue = ValidatorType<ReturnType<typeof categoryValidator>>;

const commonTimeEntryValidator = (column: CommonTimeEntryCol) =>
    pipe(
        column.format,
        timeEntryValidator,
        wrapValidatedValueWithKind(column.kind)
    );
type CommonTimeEntryValue = ValidatorType<
    ReturnType<typeof commonTimeEntryValidator>
>;

// TODO some potential to reduce valueTypes count? category, team can be represented as CommonText? etc.
export type KnownCellValue =
    | FinishTimeValue
    | StartTimeValue
    | BornValue
    | ForenameSurnameValue
    | SurnameForenameValue
    | ForenameValue
    | SurnameValue
    | BibValue
    | RaceNumberValue
    | SexValue
    | RankValue
    | CategoryValue
    | TeamValue
    | CommonTextValue
    | CommonNumberValue
    | CommonTimeEntryValue;

export const cellValueValidatorFromImportedColumn = (
    column: ImportedColumn
): Option<Validator<KnownCellValue>> => {
    switch (column.kind) {
        case "skip":
            return option.none;
        case "finishTime":
            return pipe(column, finishTimeValidator, option.some);
        case "startTime":
            return pipe(column, startTimeValidator, option.some);
        case "forenameSurname":
            return pipe(column, forenameSurnameValidator, option.some);
        case "surnameForename":
            return pipe(column, surnameForenameValidator, option.some);
        case "forename":
            return pipe(column, forenameValidator, option.some);
        case "surname":
            return pipe(column, surnameValidator, option.some);
        case "raceNumber":
            return pipe(column, raceNumberValidator, option.some);
        case "bib":
            return pipe(column, bibValidator, option.some);
        case "born":
            return pipe(column, bornValidator, option.some);
        case "sex":
            return pipe(column, sexValidator, option.some);
        case "team":
            return pipe(column, teamValidator, option.some);
        case "rank":
            return pipe(column, rankValidator, option.some);
        case "category":
            return pipe(column, categoryValidator, option.some);
        case "commonText":
            return pipe(column, commonTextValidator, option.some);
        case "commonNumber":
            return pipe(column, commonNumberValidator, option.some);
        case "commonTimeEntry":
            return pipe(column, commonTimeEntryValidator, option.some);
    }

    assertUnreachable(column);
};

// SYNC with db: CREATE TYPE public.result_unfinished_reason AS ENUM('dns', 'dnf', 'dsq');
export const unfinishedReasonLabels = messages(
    {
        dns: "DNS",
        dnf: "DNF",
        dsq: "DSQ",
    },
    {
        cs: {
            dns: "DNS",
            dnf: "DNF",
            dsq: "DSQ",
        },
    }
);
const unfinishedReasonCodec = t.keyof(unfinishedReasonLabels.en);
export type UnfinishedReason = t.TypeOf<typeof unfinishedReasonCodec>;
export type FinishTimeEntry =
    | { kind: "finished"; timeEntry: StoredTimeEntry }
    | { kind: "unfinished"; reason: UnfinishedReason };

const commonColNameCodec = NonEmptyString; // object key can be any js valid string... so no specific escaping needed
const commonColValueCodec = t.union([t.number, t.string, t.null]); // time, boolean, enum value? all can be basically encoded in this three types
const commonColCodec = t.record(commonColNameCodec, commonColValueCodec);

export const raceNumberStringCodec = t.union([
    NumberFromString,
    NonEmptyString,
]);
export const raceNumberCodec = t.union(
    [t.number, raceNumberStringCodec],
    "RaceNumber"
);
export type RaceNumber = t.TypeOf<typeof raceNumberCodec>;
export const raceNumberEq: Eq<RaceNumber> = {
    equals: (x, y) => string.Eq.equals(String(x), String(y)),
};
export const raceNumberOrd: Ord<RaceNumber> = {
    equals: raceNumberEq.equals,
    compare: (first, second) => {
        const isFirstNumber = t.number.is(first);
        if (isFirstNumber && t.number.is(second)) {
            return number.Ord.compare(first, second);
        } else if (t.string.is(first) && t.string.is(second)) {
            return string.Ord.compare(first, second);
        } else if (isFirstNumber) {
            // second has to be string therefore
            return -1;
        } else {
            return 1;
        }
    },
};

const bibCodec = NonEmptyString;

export const resultsTable = table("results", {
    id: commonIdCodec,
    start_time: nullable(storedTimeEntryCodec),
    finish_time: nullable(storedTimeEntryCodec),
    sex: nullable(sexCodec),
    born: nullable(bornCodec),
    category: nullable(NonEmptyString),
    bib: nullable(bibCodec), //  TODO rename to chip?!
    number: nullable(raceNumberCodec), // Rename to race_number / bib?
    race_id: raceTable.columns.id,
    category_id: nullable(categoriesTable.columns.id),
    name: nullable(fullNameCodec),
    club: nullable(NonEmptyString),
    club_id: nullable(clubsTable.columns.id),
    unfinished_reason: nullable(unfinishedReasonCodec),
    rank: nullable(rankCodec),
    serial_points: nullable(serialPointsCodec), // TODO serial => series, add thos column to dynamic... what for multiple serials? (overall, sprinter, mountainer...) best way would be store it in common cols
    organization_athlete_id: nullable(organizationAthleteTable.columns.id),
    laps_completed: nullable(pgSmallintCodec),
    // common: nullable(commonColCodec), TODO
});
export type ResultRow = TableRowT<typeof resultsTable>;

export const resultsWithCategoryTitleView = table(
    "results_with_category_title", // TODO better naming? there is also club title, result time etc..
    {
        ...resultsTable.columns,
        category_title: nullable(NonEmptyString),
        club_title: nullable(NonEmptyString), // TODO remove!, https://github.com/pompep/xathlo/issues/24
        time: nullable(storedTimeEntryCodec),
    }
); // TODO pg ts view definition?

export type ResultWithCategoryTitle = TableRowT<
    typeof resultsWithCategoryTitleView
>;
export const resultMessages = messages(
    {
        start_time: "Start time",
        finish_time: "Finish time",
        time: "Result time",
        sex: "Sex",
        race: "Race",
        born: "Born",
        category: "Category",
        bib: "Chip",
        number: "Race number", // TODO rename to chip
        name: "Full name",
        club: "Club",
        unfinished_reason: "Unfinished reason",
        rank: "Rank",
        serialPoints: "Serial points",
        laps_completed: "Laps (finished)",
    },
    {
        cs: {
            race: "Závod",
            start_time: "Čas startu",
            finish_time: "Čas v cíli",
            time: "Výsledný čas",
            sex: "Pohlaví",
            born: "Ročník",
            category: "Kategorie",
            bib: "Čip",
            number: "Startovní číslo",
            name: "Jméno",
            club: "Klub",
            unfinished_reason: "Důvod neklasifikace",
            rank: "Pořadí",
            serialPoints: "Body seriálu",
            laps_completed: "Okruhy (dokončené)",
        },
    }
);

export const finishTimeEntryValidator =
    (commonTimeEntryValidator: ReturnType<typeof timeEntryValidator>) =>
    (cellValue: unknown) =>
        pipe(cellValue, commonTimeEntryValidator, (validationResult) => {
            return pipe(
                validationResult,
                either.fold(
                    (e) =>
                        pipe(
                            String(cellValue).toLowerCase(),
                            unfinishedReasonCodec.decode,
                            either.mapLeft(() => e),
                            either.map(
                                (reason): FinishTimeEntry => ({
                                    kind: "unfinished",
                                    reason,
                                })
                            )
                        ),
                    (timeEntry) => {
                        const res: FinishTimeEntry = {
                            timeEntry,
                            kind: "finished",
                        };

                        return either.right(res);
                    }
                )
            );
        });

export const importedRowToDbResultRow =
    (
        validators: Record<OriginalColName, Validator<KnownCellValue>>,
        raceId: RaceId
    ) =>
    (row: ImportedRow) => {
        return pipe(
            row.raw,
            record.filterMapWithIndex((column, rawCellValue) =>
                pipe(
                    validators,
                    record.lookup(column),
                    option.chain(flow(apply(rawCellValue), option.fromEither))
                )
            ),
            (rawRowMapped): Omit<ResultRow, "id"> => {
                const finishTime = findKnownValue<FinishTimeValue>(
                    rawRowMapped,
                    "finishTime"
                );
                return {
                    name: pipe(
                        findKnownValue<ForenameSurnameValue>(
                            rawRowMapped,
                            "forenameSurname"
                        ),
                        optionOrChain(() =>
                            findKnownValue<SurnameForenameValue>(
                                rawRowMapped,
                                "surnameForename"
                            )
                        ),
                        optionOrChain(() =>
                            pipe(
                                {
                                    forename: findKnownValue<ForenameValue>(
                                        rawRowMapped,
                                        "forename"
                                    ),
                                    surname: findKnownValue<SurnameValue>(
                                        rawRowMapped,
                                        "surname"
                                    ),
                                },
                                sequenceS(option.Apply),
                                option.map((s) => s.forename + " " + s.surname),
                                option.chain(
                                    flow(
                                        fullNameCodec.decode,
                                        option.fromEither
                                    )
                                )
                            )
                        ),
                        option.toNullable
                    ),
                    born: pipe(
                        findKnownValue<BornValue>(rawRowMapped, "born"),
                        option.toNullable
                    ),
                    bib: pipe(
                        findKnownValue<BibValue>(rawRowMapped, "bib"),
                        option.toNullable
                    ),
                    number: pipe(
                        findKnownValue<RaceNumberValue>(
                            rawRowMapped,
                            "raceNumber"
                        ),
                        option.toNullable
                    ),
                    start_time: pipe(
                        findKnownValue<StartTimeValue>(
                            rawRowMapped,
                            "startTime"
                        ),
                        option.toNullable
                    ),
                    rank: pipe(
                        findKnownValue<RankValue>(rawRowMapped, "rank"),
                        option.toNullable
                    ),
                    laps_completed: null, // TODO
                    category: null, // TODO later
                    serial_points: null, // TODO later
                    organization_athlete_id: null, // filled by trigger for now
                    category_id: row.categoryId,
                    club: pipe(
                        findKnownValue<TeamValue>(rawRowMapped, "team"),
                        option.toNullable
                    ),
                    club_id: null, // TODO
                    finish_time: pipe(
                        finishTime,
                        option.chain((v) => {
                            switch (v.kind) {
                                case "finished":
                                    return option.some(v.timeEntry);
                                case "unfinished":
                                    return option.none;
                            }
                            assertUnreachable(v);
                        }),
                        option.toNullable
                    ),
                    sex: pipe(
                        findKnownValue<SexValue>(rawRowMapped, "sex"),
                        option.toNullable
                    ),
                    race_id: raceId,
                    unfinished_reason: pipe(
                        finishTime,
                        option.chain((v) => {
                            switch (v.kind) {
                                case "finished":
                                    return option.none;
                                case "unfinished":
                                    return option.some(v.reason);
                            }
                            assertUnreachable(v);
                        }),
                        option.toNullable
                    ),
                    // common: null, // TODO! commonText, commonNumber, commonTime values
                };
            }
        );
    };

const findKnownValue = <T extends KnownCellValue>(
    row: Record<OriginalColName, KnownCellValue>,
    kind: T["kind"]
): Option<T["value"]> =>
    pipe(
        row,
        record.filter((knownCellValue) => knownCellValue.kind === kind),
        record.toArray,
        array.head,
        option.map(([_, knownCellValue]) => knownCellValue.value)
    );

export type WithUnfinishedReason = Pick<ResultRow, "unfinished_reason">;
export const isResultClassified = (result: WithUnfinishedReason) =>
    result.unfinished_reason == null;

export const ordByUnfinishedReason = pipe(
    string.Ord,
    option.getOrd,
    contramap((r: WithUnfinishedReason) =>
        option.fromNullable(r.unfinished_reason)
    )
);

export type WithTime = Pick<ResultWithCategoryTitle, "time">;
export const ordByTime = pipe(
    number.Ord,
    getOptionReverseOrd,
    contramap((r: WithTime) => option.fromNullable(r.time))
);

export type WithRaceId = Pick<ResultRow, "race_id">;
export const ordByRaceId = (raceIdsOrdered: Array<RaceId>): Ord<WithRaceId> =>
    ord.fromCompare((first, second) =>
        optNumberReverseOrd.compare(
            pipe(
                raceIdsOrdered,
                array.findIndex((i) => i === first.race_id)
            ),
            pipe(
                raceIdsOrdered,
                array.findIndex((i) => i === second.race_id)
            )
        )
    );

export type WithRank = Pick<ResultRow, "rank">;
export const ordByRank = pipe(
    number.Ord,
    getOptionReverseOrd,
    contramap((r: WithRank) => option.fromNullable(r.rank))
);

export type WithSerialPoints = Pick<ResultRow, "serial_points">;
export const ordBySerialPointsDesc = pipe(
    number.Ord,
    option.getOrd,
    contramap((r: WithSerialPoints) => option.fromNullable(r.serial_points)),
    ord.reverse
);

export type WithLapsCompleted = Pick<ResultRow, "laps_completed">;
export const ordByLapsCompletedDesc = pipe(
    number.Ord,
    option.getOrd,
    contramap((r: WithLapsCompleted) => option.fromNullable(r.laps_completed)),
    ord.reverse
);

const zeroPoints = pipe(0, serialPointsCodec.decode, rightOrThrow);
export const zeroPointsFallback = () => zeroPoints;
